import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getUserResumes, getResumeDetails, getUserDetails, getAllResumes, debugDatabaseStructure } from '../../utils/userManagementService';
import { checkAdminAccess, requireAdminAccess } from '../../utils/adminService';
import { supabase } from '../../lib/supabase';
import { ResumeRenderer } from '../../components/resume';
import { ArrowLeft, FileText, Eye, Calendar, User, Mail, Phone, MapPin, Github, Star, CheckCircle, XCircle, Shield, AlertTriangle, ZoomIn, ZoomOut, Download, ExternalLink, X, MessageCircle, Coffee } from 'lucide-react';
import { AdminLayout, AdminAccessDenied } from '../../components/admin/layout';
import '../../styles/common/latte-components.css';
import '../../styles/common/latte-utilities.css';

const UserResumeManager = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [resumes, setResumes] = useState([]);
  const [portfolios, setPortfolios] = useState({});
  const [selectedResume, setSelectedResume] = useState(null);
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [userInfo, setUserInfo] = useState(null);
  const [showResumeModal, setShowResumeModal] = useState(false);
  const [error, setError] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [accessDenied, setAccessDenied] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(100);
  const [chatMessages, setChatMessages] = useState([]);
  const [showChatModal, setShowChatModal] = useState(false);
  const [selectedSessionId, setSelectedSessionId] = useState(null);
  const [loadingMessages, setLoadingMessages] = useState(false);

  useEffect(() => {
    checkAccess();
  }, []);

  const checkAccess = async () => {
    try {
      const adminAccess = await checkAdminAccess();
      setIsAdmin(adminAccess);
      
      if (adminAccess && userId) {
        await loadUserResumes();
      } else if (!adminAccess) {
        setAccessDenied(true);
        setLoading(false);
      }
    } catch (error) {
      console.error('권한 확인 실패:', error);
      setAccessDenied(true);
      setLoading(false);
    }
  };

  // 세션별 포트폴리오 조회
  const loadSessionPortfolios = async (sessionIds) => {
    try {
      if (!sessionIds || sessionIds.length === 0) return;
            
      const { data: portfolioData, error: portfolioError } = await supabase
        .from('portfolios')
        .select('session_id, slug, status')
        .in('session_id', sessionIds)
        .eq('status', 'published');
      
      if (portfolioError) {
        console.error('포트폴리오 조회 실패:', portfolioError);
      } else {
        
        // 세션 ID를 키로 하는 객체로 변환
        const portfolioMap = {};
        portfolioData?.forEach(portfolio => {
          if (portfolio.session_id) {
            portfolioMap[portfolio.session_id] = portfolio;
          }
        });
        
        setPortfolios(portfolioMap);
      }
    } catch (error) {
      console.error('포트폴리오 목록 로딩 실패:', error);
    }
  };

  const loadUserResumes = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // 관리자 권한 재확인
      await requireAdminAccess();
            
      // 간단한 테스트: 해당 ID가 profiles에 있는지 확인
      const { data: profileCheck, error: profileCheckError } = await supabase
        .from('profiles')
        .select('id, email, name')
        .eq('id', userId)
        .single();
            
      // 만약 해당 ID가 profiles에 없다면, 이메일로 찾아보기
      if (profileCheckError || !profileCheck) {
        
        // RPC에서 해당 ID의 이메일 찾기
        const { data: rpcUsers, error: rpcError } = await supabase.rpc('get_all_users_for_admin');
        if (rpcUsers) {
          const targetUser = rpcUsers.find(user => user.user_id === userId);
          if (targetUser) {
            
            // 전체 profiles 데이터에서 이메일 매칭
            const { data: allProfiles, error: allProfilesError } = await supabase.rpc('get_all_users_for_admin');
            
            if (allProfiles) {
              // 이메일로 매칭되는 사용자 찾기
              const matchingUser = allProfiles.find(user => user.email === targetUser.email);
              
              if (matchingUser) {
                
                // 전체 이력서에서 해당 이메일을 가진 user_id 찾기
                const allResumes = await getAllResumes();
                const resumeWithMatchingEmail = allResumes.find(resume => resume.email === targetUser.email);
                
                if (resumeWithMatchingEmail) {
                  const correctProfilesId = resumeWithMatchingEmail.user_id;
                  
                  // 올바른 ID로 이력서 조회
                  const resumeData = await getUserResumes(correctProfilesId);
                  
                  setResumes(resumeData);
                  setUserInfo({
                    name: resumeWithMatchingEmail.name || targetUser.display_name || '사용자',
                    email: targetUser.email
                  });
                  
                  // 세션별 포트폴리오 조회
                  const sessionIds = resumeData.map(r => r.session_id).filter(Boolean);
                  await loadSessionPortfolios(sessionIds);
                  
                  return; // 성공적으로 종료
                }
              }
            }
          }
        }
      }
      
      // 데이터베이스 구조 확인
      const dbStructure = await debugDatabaseStructure();
      
      // 디버깅: 전체 이력서 목록도 조회
      const allResumes = await getAllResumes();
      
      const resumeData = await getUserResumes(userId);
      
      setResumes(resumeData);
      
      // 세션별 포트폴리오 조회
      const sessionIds = resumeData.map(r => r.session_id).filter(Boolean);
      await loadSessionPortfolios(sessionIds);
      
      // 첫 번째 이력서에서 사용자 정보 추출
      if (resumeData.length > 0) {
        setUserInfo({
          name: resumeData[0].name,
          email: resumeData[0].email
        });
      } else {
        // 이력서가 없는 경우 사용자 정보를 다른 방식으로 가져온다
        try {
          const userDetail = await getUserDetails(userId);
          setUserInfo({
            name: userDetail?.name || '사용자',
            email: userDetail?.email || ''
          });
        } catch (detailError) {
          console.error('사용자 상세 정보 조회 실패:', detailError);
          setUserInfo({
            name: '사용자',
            email: ''
          });
        }
      }
    } catch (error) {
      console.error('이력서 목록 로딩 실패:', error);
      if (error.message?.includes('Admin privileges required')) {
        setAccessDenied(true);
        setError('관리자 권한이 필요합니다.');
      } else {
        setError('이력서 목록을 불러오는데 실패했습니다.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleViewChatMessages = async (sessionId) => {
    try {
      setLoadingMessages(true);
      setSelectedSessionId(sessionId);
      
      const { data, error } = await supabase
        .from('chat_messages')
        .select('*')
        .eq('session_id', sessionId)
        .order('created_at', { ascending: true });
        
      if (error) {
        console.error('채팅 메시지 조회 실패:', error);
        alert('채팅 메시지를 불러오는데 실패했습니다.');
      } else {
        setChatMessages(data || []);
        setShowChatModal(true);
      }
    } catch (error) {
      console.error('채팅 메시지 로딩 실패:', error);
      alert('채팅 메시지를 불러오는데 실패했습니다.');
    } finally {
      setLoadingMessages(false);
    }
  };

  const handleViewResume = async (resumeId) => {
    try {
      setDetailLoading(true);
      const resumeDetail = await getResumeDetails(resumeId);
      
      // 이력서 데이터를 ResumeRenderer가 기대하는 형식으로 변환
      const transformedResumeData = {
        name: resumeDetail.name || '',
        email: resumeDetail.email || '',
        phone: resumeDetail.phone || '',
        address: resumeDetail.address || '',
        github: resumeDetail.github || '',
        title: resumeDetail.title || '',
        summary: resumeDetail.summary || '',
        keyStrengths: resumeDetail.key_strengths || [],
        experiences: resumeDetail.experiences || [],
        education: resumeDetail.education || [],
        skills: resumeDetail.skills || [],
        certificates: resumeDetail.certificates || [],
        additionalInfo: [], // DB에 없는 필드이므로 빈 배열로 초기화
      };
      
      setSelectedResume(transformedResumeData);
      setShowResumeModal(true);
    } catch (error) {
      console.error('이력서 상세 정보 로딩 실패:', error);
      alert('이력서 상세 정보를 불러오는데 실패했습니다.');
    } finally {
      setDetailLoading(false);
    }
  };

  const handleZoomIn = () => setZoomLevel(prev => Math.min(prev + 10, 150));
  const handleZoomOut = () => setZoomLevel(prev => Math.max(prev - 10, 50));

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('ko-KR');
  };

  if (accessDenied) {
    return <AdminAccessDenied />;
  }

  const BackButton = (
    <button
      onClick={() => navigate('/admin-users')}
      className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
    >
      <ArrowLeft className="w-5 h-5" />
      <span>사용자 관리로 돌아가기</span>
    </button>
  );

  return (
    <AdminLayout
      title={`${userInfo?.name || '사용자'}의 이력서 관리`}
      description={userInfo?.email ? `${userInfo.email} 사용자가 작성한 이력서 목록입니다.` : '사용자가 작성한 이력서 목록입니다.'}
      icon={<FileText className="h-8 w-8 text-blue-600" />}
      actions={BackButton}
      isAdmin={isAdmin}
      loading={loading}
    >
      {/* 에러 메시지 */}
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <XCircle className="h-5 w-5 text-red-400" />
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">오류 발생</h3>
              <p className="mt-1 text-sm text-red-700">{error}</p>
              <button
                onClick={loadUserResumes}
                className="mt-2 text-sm font-medium text-red-600 hover:text-red-500"
              >
                다시 시도
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 이력서 목록 */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900 flex items-center">
            <FileText className="w-5 h-5 mr-2" />
            작성된 이력서 ({resumes.length}개
            {resumes.filter(r => r.deleted_at).length > 0 && (
              <span className="text-gray-500">
                , 삭제 {resumes.filter(r => r.deleted_at).length}건
              </span>
            )})
          </h3>
        </div>
        
        {resumes.length === 0 ? (
          <div className="p-12 text-center">
            <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">작성된 이력서가 없습니다.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    이력서 정보
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    직무 제목
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    진행률
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    생성일
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    수정일
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    삭제일
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    포트폴리오
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    작업
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {resumes.map((resume) => (
                  <React.Fragment key={resume.id}>
                    <tr className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900 flex items-center gap-2">
                          {resume.name || '이름 없음'}
                          {resume.deleted_at && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
                              삭제됨
                            </span>
                          )}
                        </div>
                        <div className="text-sm text-gray-500">
                          {resume.email || '이메일 없음'}
                        </div>
                        {resume.session_id && (
                          <div className="text-xs text-blue-600 mt-1">
                            세션 ID: {resume.session_id}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {resume.title || '직무 제목 없음'}
                      </div>
                      {resume.summary && (
                        <div className="text-xs text-gray-500 mt-1 max-w-xs truncate">
                          {resume.summary}
                        </div>
                      )}
                    </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="w-16 bg-gray-200 rounded-full h-2 mr-2">
                          <div
                            className="bg-blue-500 h-2 rounded-full"
                            style={{ width: `${resume.session_progress || 0}%` }}
                          ></div>
                        </div>
                        <span className="text-sm text-gray-600">{resume.session_progress || 0}%</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(resume.created_at)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(resume.updated_at)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {resume.deleted_at ? (
                        <span>
                          {formatDate(resume.deleted_at)}
                        </span>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {resume.session_id && portfolios[resume.session_id] ? (
                        <a
                          href={`/${portfolios[resume.session_id].slug}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center space-x-1 text-blue-600 hover:text-blue-900 px-2 py-1 border border-blue-300 rounded-md hover:bg-blue-50 transition-colors text-xs"
                          title="웹 페이지 보기"
                        >
                          <ExternalLink size={14} />
                          <span>웹 페이지 열기</span>
                        </a>
                      ) : (
                        <span className="text-gray-400">없음</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleViewResume(resume.id)}
                          disabled={detailLoading}
                          className="inline-flex items-center px-3 py-1.5 border border-green-600 text-green-600 bg-white rounded-md hover:bg-green-50 transition-colors text-xs disabled:opacity-50"
                          title="이력서 상세 보기"
                        >
                          {detailLoading ? (
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-600"></div>
                          ) : (
                            <>
                              <Eye className="h-4 w-4 mr-1.5" />
                              이력서 보기
                            </>
                          )}
                        </button>
                        {resume.session_id && (
                          <button
                            onClick={() => handleViewChatMessages(resume.session_id)}
                            disabled={loadingMessages}
                            className="inline-flex items-center px-3 py-1.5 border border-blue-600 text-blue-600 bg-white rounded-md hover:bg-blue-50 transition-colors text-xs disabled:opacity-50"
                            title="채팅 내역 보기"
                          >
                            {loadingMessages ? (
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                            ) : (
                              <>
                                <MessageCircle className="h-4 w-4 mr-1.5" />
                                채팅 내역
                              </>
                            )}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* 채팅 내역 모달 */}
      {showChatModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-4 mx-auto p-6 border w-[95%] max-w-[900px] min-h-[80vh] shadow-lg rounded-md bg-white">
            <div className="flex flex-col h-full">
              {/* 모달 헤더 */}
              <div className="flex justify-between items-center mb-6 border-b pb-4">
                <h3 className="text-xl font-bold text-gray-900 flex items-center">
                  <MessageCircle className="w-6 h-6 mr-2" />
                  채팅 내역
                  <span className="ml-2 text-sm font-normal text-gray-500">
                    (세션 ID: {selectedSessionId})
                  </span>
                </h3>
                <div className="flex items-center gap-3">
                  <span className="text-sm text-gray-600">
                    총 {chatMessages.length}개 메시지
                  </span>
                  <button
                    onClick={() => {
                      setShowChatModal(false);
                      setChatMessages([]);
                      setSelectedSessionId(null);
                    }}
                    className="inline-flex items-center px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
                    title="닫기"
                  >
                    <X size={18} className="mr-1.5" />
                    닫기
                  </button>
                </div>
              </div>
              
              {/* 채팅 메시지 영역 */}
              <div className="flex-1 overflow-y-auto max-h-[65vh] bg-gray-50 rounded-lg p-4">
                {chatMessages.length > 0 ? (
                  <div className="space-y-4">
                    {chatMessages.map((message, idx) => {
                      // content가 문자열인지 객체인지 확인
                      let messageContent = '';
                      if (typeof message.content === 'string') {
                        messageContent = message.content;
                      } else if (typeof message.content === 'object' && message.content !== null) {
                        // JSON 객체인 경우 text 필드를 찾거나 JSON 문자열로 변환
                        messageContent = message.content.text || message.content.message || JSON.stringify(message.content);
                      }
                      
                      // sender 필드로 사용자 구분
                      const isUser = message.sender === 'user';
                      
                      return (
                        <div key={message.id || idx} className={`flex ${isUser ? 'justify-end' : 'justify-start'} mb-4`}>
                          <div className={`flex items-start gap-2 max-w-[70%] ${isUser ? 'flex-row-reverse' : ''}`}>
                            <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
                              isUser ? 'bg-blue-500' : 'bg-gray-300'
                            }`}>
                              {isUser ? (
                                <User className="w-4 h-4 text-white" />
                              ) : (
                                <Coffee className="w-4 h-4 text-gray-600" />
                              )}
                            </div>
                            
                            <div className={`px-4 py-2 rounded-lg ${
                              isUser ? 'bg-blue-500 text-white' : 'bg-white text-gray-800'
                            } shadow-sm`}>
                              <p className="text-sm leading-relaxed whitespace-pre-wrap break-words">
                                {messageContent}
                              </p>
                              <div className={`text-xs mt-1 ${
                                isUser ? 'text-blue-100' : 'text-gray-500'
                              }`}>
                                {new Date(message.created_at).toLocaleTimeString('ko-KR')}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 text-center py-8">
                    채팅 메시지가 없습니다.
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 이력서 상세보기 모달 - ResumeRenderer 사용 */}
      {showResumeModal && selectedResume && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-4 mx-auto p-6 border w-[95%] max-w-[1200px] shadow-lg rounded-md bg-white min-h-[calc(100vh-2rem)]">
            <div className="flex flex-col h-full">
              {/* 모달 헤더 */}
              <div className="flex justify-between items-center mb-6 border-b pb-4">
                <h3 className="text-xl font-bold text-gray-900 flex items-center">
                  <FileText className="w-6 h-6 mr-2" />
                  {selectedResume.name}님의 이력서
                </h3>
                <div className="flex items-center gap-3">
                  {/* 줌 컨트롤 */}
                  <div className="flex items-center gap-2">
                    <button 
                      onClick={handleZoomOut} 
                      className="inline-flex items-center px-3 py-1.5 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50" 
                      title="축소"
                    >
                      <ZoomOut size={16} className="mr-1" />
                      축소
                    </button>
                    <span className="text-sm text-gray-600 min-w-[3rem] text-center tabular-nums">
                      {zoomLevel}%
                    </span>
                    <button 
                      onClick={handleZoomIn} 
                      className="inline-flex items-center px-3 py-1.5 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50" 
                      title="확대"
                    >
                      <ZoomIn size={16} className="mr-1" />
                      확대
                    </button>
                  </div>
                  
                  <div className="border-l border-gray-300 h-6"></div>
                  
                  {/* 닫기 버튼 */}
                  <button
                    onClick={() => setShowResumeModal(false)}
                    className="inline-flex items-center px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
                    title="닫기"
                  >
                    <X size={18} className="mr-1.5" />
                    닫기
                  </button>
                </div>
              </div>
              
              {/* 이력서 렌더링 영역 */}
              <div className="flex-1 overflow-hidden">
                <ResumeRenderer
                  resumeData={selectedResume}
                  updatingField={null}
                  zoomLevel={zoomLevel}
                  containerMode="fixed"
                  enableAutoScale={false}
                  className="h-full"
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default UserResumeManager;
